import React, { useEffect, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import { format } from 'date-fns';

import { StateLanguage } from '../../../../../languages/config/StateLanguage';
import { Currency } from '../../../../../models/currency.model';
import { AccountingEntry } from '../../../../../models/accounting.entry.model';
import { TreasuryAccount } from '../../../../../models/treasury.account.model';
import { PosCashier } from '../../../../../models/pos.model';
import { suffixLegend } from '../../../../../scripts/name.legend.script';
import { formatAccountingEntry, formatMoney } from '../../../../../scripts/format.value.script';
import { legendIconInvalid, legendInvalid, legendInvalidRequired, legendValid, legendValidRequired, legendValidRequiredRestart } from '../../../../../utils/legend.data.util';
import { expressions } from '../../../../../libraries/regular.expressions.library';
import { evaluateLegendListObjectRequiredIcon } from '../../../../../scripts/validate.legend.script';
import { isValidObjectAttributes } from '../../../../../scripts/compare.object.script';
import { paymentMethodValue } from '../../../../../libraries/payment.library';
import { ExchangeRateDA } from '../../../../../services/admin/exchange.rate.service';

import AppLegend from '../../../../../components/element/Legend';

interface PaymentValue {
  method: string;
  id_accounting_entry: any;
  amount: string;
  received: string;
  change: string;
  currency_code: string;
  amount_chf: string;
  exchange_rate: string;
  exchange_date: string;
  id_treasury_account: string;
  change_amount: string;
  change_currency_code: string;
}

interface PaymentValid {
  id_accounting_entry: boolean;
  amount: boolean;
  received: boolean;
}

export interface PosPaymentFormProps {
  companyId: string,
  companyCurrency: Currency,
  totalAmount: number,
  customerValue: string,
  treasuryAccountsForCompany: TreasuryAccount[],
  accountingCreditEntries: AccountingEntry[],
  posCashier: PosCashier,
  onPaymentsChange: (data: {
    payments: {list: boolean, value: PaymentValue[], valid: PaymentValid[]},
    isValid: boolean
  }) => void
}

const PosPaymentForm: React.FunctionComponent<PosPaymentFormProps> = ({companyId, companyCurrency, totalAmount, customerValue, treasuryAccountsForCompany, accountingCreditEntries, posCashier, onPaymentsChange}) => {
  const {lang} = StateLanguage()

  const [payments, setPayments] = useState({list: false, value: [] as PaymentValue[], valid: [] as PaymentValid[]})
  const [ratesCache, setRatesCache] = useState<Record<string, {rate: number, date: string, stale: boolean}>>({})
  const [rateStaleWarning, setRateStaleWarning] = useState<string | null>(null)

  const fetchExchangeRate = async (targetCurrency: string): Promise<{rate: number, date: string} | null> => {
    if (targetCurrency === 'CHF') return {rate: 1, date: format(new Date(), 'yyyy-MM-dd')}

    const cached = ratesCache[targetCurrency]
    const today = format(new Date(), 'yyyy-MM-dd')
    if (cached && cached.date === today) {
      if (cached.stale) setRateStaleWarning(`${lang.labels.exchangeRate ?? 'Exchange rate'}: ${cached.date}`)
      return {rate: cached.rate, date: cached.date}
    }

    try {
      const response = await ExchangeRateDA.getRate(companyId, 'CHF', targetCurrency)
      if (response.status === 200) {
        const data = response.data
        setRatesCache(prev => ({...prev, [targetCurrency]: {rate: data.rate, date: data.date, stale: data.stale}}))
        if (data.stale) {
          setRateStaleWarning(`${lang.labels.exchangeRate ?? 'Exchange rate'}: ${data.date}`)
        } else {
          setRateStaleWarning(null)
        }
        return {rate: data.rate, date: data.date}
      }
    } catch {}
    return null
  }

  const resolveTreasuryAccount = (ref: any): TreasuryAccount | undefined => {
    if (!ref) return undefined
    if (typeof ref === 'object' && ref.id) return ref as TreasuryAccount
    if (typeof ref === 'string') return treasuryAccountsForCompany.find(a => a.id === ref)
    return undefined
  }

  const cashAccounts: TreasuryAccount[] = (posCashier.treasury_cash_accounts ?? [])
    .map((entry: any) => resolveTreasuryAccount(entry))
    .filter(Boolean) as TreasuryAccount[]
  const cardAccount = resolveTreasuryAccount(posCashier.treasury_card)
  const defaultCurrency = posCashier.default_cash_currency || 'CHF'

  function getCashAccountForCurrency(currencyCode: string): TreasuryAccount | undefined {
    return cashAccounts.find(a => a.currency === currencyCode)
  }

  function getAvailableCashCurrencies(): string[] {
    return cashAccounts.map(a => a.currency)
  }

  const handleChangeCurrencyPayment = async (index: number, currencyCode: string) => {
    const account = getCashAccountForCurrency(currencyCode)
    let rate: string = '1'
    let rateDate: string = format(new Date(), 'yyyy-MM-dd')

    if (currencyCode !== 'CHF') {
      const fetched = await fetchExchangeRate(currencyCode)
      if (fetched) {
        rate = fetched.rate.toString()
        rateDate = fetched.date
      } else {
        rate = ''
      }
    }

    let tempPaymentsValue = payments.value.map((temp_item, temp_index) => {
      if (index === temp_index && temp_item.method === 'cash') {
        const amountChfBefore = Number(temp_item.amount_chf) || Number(temp_item.amount) || 0
        temp_item.currency_code = currencyCode
        temp_item.id_treasury_account = account?.id ?? ''
        temp_item.change_currency_code = 'CHF'
        temp_item.exchange_rate = rate
        temp_item.exchange_date = rateDate
        if (currencyCode === 'CHF') {
          temp_item.amount = amountChfBefore.toFixed(2)
          temp_item.received = amountChfBefore.toFixed(2)
          temp_item.amount_chf = amountChfBefore.toFixed(2)
        } else {
          const rateNum = Number(rate) || 0
          const convertedAmount = rateNum > 0 ? (amountChfBefore / rateNum).toFixed(2) : '0.00'
          temp_item.amount = convertedAmount
          temp_item.received = convertedAmount
          temp_item.amount_chf = (Number(convertedAmount) * rateNum).toFixed(2)
        }
        temp_item.change = '0'
        temp_item.change_amount = '0'
      }
      return temp_item
    })
    setPayments({...payments, value: tempPaymentsValue})
  }

  const handleChangeExchangeRate = (index: number, event: React.ChangeEvent<HTMLInputElement>) => {
    let tempPaymentsValue = payments.value.map((temp_item, temp_index) => {
      if (index === temp_index) {
        temp_item.exchange_rate = event.target.value
        const rate = Number(event.target.value) || 0
        temp_item.amount_chf = (Number(temp_item.amount) * rate).toFixed(2)
      }
      return temp_item
    })
    setPayments({...payments, value: tempPaymentsValue})
  }

  const handleChangeExchangeDate = (index: number, event: React.ChangeEvent<HTMLInputElement>) => {
    let tempPaymentsValue = payments.value.map((temp_item, temp_index) => {
      if (index === temp_index) {
        temp_item.exchange_date = event.target.value
      }
      return temp_item
    })
    setPayments({...payments, value: tempPaymentsValue})
  }

  const handleChangeChangeCurrency = (index: number, currencyCode: string) => {
    let tempPaymentsValue = payments.value.map((temp_item, temp_index) => {
      if (index === temp_index) {
        temp_item.change_currency_code = currencyCode
      }
      return temp_item
    })
    setPayments({...payments, value: tempPaymentsValue})
  }

  const formatChangeAmount = (payment: PaymentValue): string => {
    const received = Number(payment.received) || 0
    const amount = Number(payment.amount) || 0
    const changeInPaymentCurrency = received - amount
    if (changeInPaymentCurrency <= 0) return '0.00'
    if (payment.currency_code === 'CHF' || payment.change_currency_code === payment.currency_code) {
      return formatMoney(changeInPaymentCurrency)
    }
    const exchange_rate = Number(payment.exchange_rate) || 1
    return formatMoney(changeInPaymentCurrency * exchange_rate)
  }

  const handleChangeAccountingEntry = (index: number, item: AccountingEntry) => {
    if (payments.valid[index].id_accounting_entry && payments.value[index].id_accounting_entry && payments.value[index].id_accounting_entry.id === item.id) {
      return null
    }

    let isDifferent: boolean = !payments.value[index].id_accounting_entry || payments.value[index].id_accounting_entry.id !== item.id
    let debtTotal: number = isDifferent ? (calculatorPayment().debt_total + Number(payments.value[index].amount)) : calculatorPayment().debt_total

    const debitWithTreasury = item.debits?.find(d => d?.reference?.id_treasury_account)
    const treasuryId = debitWithTreasury?.reference?.id_treasury_account ?? ''

    let tempPaymentsValue = payments.value.map((temp_item, temp_index) => {
      if (index === temp_index) {
        temp_item.id_accounting_entry = item
        temp_item.amount = ((item.balance >= debtTotal) ? debtTotal : item.balance).toFixed(2)
        temp_item.amount_chf = temp_item.amount
        temp_item.id_treasury_account = treasuryId
      }
      return temp_item
    })
    let tempPaymentsValid = payments.valid.map((temp_item, temp_index) => {
      if (index === temp_index) {
        temp_item.id_accounting_entry = true
        temp_item.amount = true
      }
      return temp_item
    })

    setPayments({...payments, value: tempPaymentsValue, valid: tempPaymentsValid})
  }

  function normalizePaymentValues(item: PaymentValue): PaymentValue {
    if (item.method !== 'cash') {
      item.received = item.amount
      item.change = '0'
      item.change_amount = '0'
    }
    return item
  }

  const handleChangeAmountPayment = (index: number, event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    let tempPaymentsValue = payments.value.map((temp_item, temp_index) => {
      if (index === temp_index) {
        temp_item.amount = event.target.value
        if (temp_item.currency_code === 'CHF') {
          temp_item.amount_chf = event.target.value
        } else {
          const rate = Number(temp_item.exchange_rate) || 0
          temp_item.amount_chf = (Number(event.target.value) * rate).toFixed(2)
        }
        if (temp_item.method === 'cash') {
          temp_item.change = (Number.isFinite(Number(temp_item.received)) && Number.isFinite(Number(event.target.value)) ? Number(temp_item.received) - Number(event.target.value) : 0).toString()
          temp_item.change_amount = temp_item.change
        } else {
          normalizePaymentValues(temp_item)
        }
      }
      return temp_item
    })

    setPayments({...payments, value: tempPaymentsValue})
  }

  const handleChangeReceivedPayment = (index: number, event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    let tempPaymentsValue = payments.value.map((temp_item, temp_index) => {
      if (index === temp_index && temp_item.method === 'cash') {
        temp_item.received = event.target.value
        temp_item.change = (Number.isFinite(Number(event.target.value)) && Number.isFinite(Number(temp_item.amount)) ? Number(event.target.value) - Number(temp_item.amount) : 0).toString()
        temp_item.change_amount = temp_item.change
      }
      return temp_item
    })

    setPayments({...payments, value: tempPaymentsValue})
  }

  const validateAmountPayment = (index: number) => {
    evaluateLegendListObjectRequiredIcon(expressions.currency, index, 'amount', payments, setPayments, `input-payment-amount${suffixLegend(index)}`, `legend-payment-amount-valid${suffixLegend(index)}`, `legend-payment-amount-required${suffixLegend(index)}`)
  }

  const validateReceivedPayment = (index: number) => {
    evaluateLegendListObjectRequiredIcon(expressions.currency, index, 'received', payments, setPayments, `input-payment-received${suffixLegend(index)}`, `legend-payment-received-valid${suffixLegend(index)}`, `legend-payment-received-required${suffixLegend(index)}`)
  }

  const executeAddPayment = async (item: 'cash' | 'card' | 'deposit') => {
    let paid: number = payments.value.reduce((sum, payment) => sum + Number(payment.amount_chf || payment.amount), 0)
    let remaining: number = (item !== 'deposit') ? Math.round((totalAmount - paid + Number.EPSILON) * 100) / 100 : 0
    let currencyCode = 'CHF'
    let treasuryAccountId = ''
    let exchangeRate = '1'
    let exchangeDate = format(new Date(), 'yyyy-MM-dd')

    if (item === 'cash') {
      currencyCode = defaultCurrency
      const account = getCashAccountForCurrency(currencyCode)
      treasuryAccountId = account?.id ?? ''
      if (currencyCode !== 'CHF') {
        const fetched = await fetchExchangeRate(currencyCode)
        if (fetched) {
          exchangeRate = fetched.rate.toString()
          exchangeDate = fetched.date
        } else {
          exchangeRate = ''
        }
      }
    } else if (item === 'card') {
      treasuryAccountId = cardAccount?.id ?? ''
    }

    let amountInCurrency = remaining
    let amountChf = remaining.toFixed(2)
    if (item === 'cash' && currencyCode !== 'CHF') {
      const rateNum = Number(exchangeRate) || 0
      if (rateNum > 0) {
        amountInCurrency = Number((remaining / rateNum).toFixed(2))
        amountChf = (amountInCurrency * rateNum).toFixed(2)
      } else {
        amountInCurrency = 0
        amountChf = '0.00'
      }
    }

    let newPaymentValue: PaymentValue = {
      method: item,
      id_accounting_entry: null,
      amount: amountInCurrency.toFixed(2),
      received: amountInCurrency.toFixed(2),
      change: '0',
      currency_code: currencyCode,
      amount_chf: amountChf,
      exchange_rate: exchangeRate,
      exchange_date: exchangeDate,
      id_treasury_account: treasuryAccountId,
      change_amount: '0',
      change_currency_code: 'CHF'
    }
    let hasValidAmount = item !== 'deposit' ? (item === 'cash' && currencyCode !== 'CHF' ? amountInCurrency > 0 : true) : true
    let newPaymentValid: PaymentValid = {id_accounting_entry: item !== 'deposit', amount: hasValidAmount, received: hasValidAmount}

    setPayments({list: true, value: [...payments.value, newPaymentValue], valid: [...payments.valid, newPaymentValid]})
    legendValid('legend-payment-required')
  }

  const executeRemovePayment = (index: number) => {
    let tempPaymentsValue = payments.value.filter((_, temp_index) => (temp_index !== index))
    let tempPaymentsValid = payments.valid.filter((_, temp_index) => (temp_index !== index))

    setPayments({list: !(tempPaymentsValue.length === 0 && tempPaymentsValid.length === 0), value: tempPaymentsValue, valid: tempPaymentsValid})

    if (tempPaymentsValue.length === 0 && tempPaymentsValid.length === 0) {
      legendInvalid('legend-payment-required')
    }

    restartFormPayment(tempPaymentsValue)
  }

  function restartFormPayment(items: PaymentValue[]) {
    if (!expressions) return

    items.forEach((item, index) => {
      if (item.method === 'cash' || item.method === 'card' || item.method === 'deposit') {
        if (expressions.currency.test(item.amount)) {
          legendValidRequired(`legend-payment-amount-valid${suffixLegend(index)}`, `legend-payment-amount-required${suffixLegend(index)}`)
        } else {
          legendInvalidRequired(item.amount, `legend-payment-amount-valid${suffixLegend(index)}`, `legend-payment-amount-required${suffixLegend(index)}`)
        }
      }
      if (item.method === 'cash' || item.method === 'card') {
        if (expressions.currency.test(item.received)) {
          legendValidRequired(`legend-payment-received-valid${suffixLegend(index)}`, `legend-payment-received-required${suffixLegend(index)}`)
        } else {
          legendInvalidRequired(item.amount, `legend-payment-received-valid${suffixLegend(index)}`, `legend-payment-received-required${suffixLegend(index)}`)
        }
      }
      if (item.method === 'deposit') {
        if (item.id_accounting_entry.id.length > 0) {
          legendValid(`legend-payment-accounting-entry-required${suffixLegend(index)}`)
        } else {
          legendInvalid(`legend-payment-accounting-entry-required${suffixLegend(index)}`)
        }
      }
    })
  }

  function calculatorPayment(): {total: number, paid_total: number, received_total: number, change_total: number, debt_total: number, paid: {cash: number, card: number, deposit: number}} {
    let total: number = totalAmount
    let paidTotal: number = 0
    let receivedTotal: number = 0
    let changeTotal: number = 0
    let debtTotal: number = total
    let paidCash: number = 0
    let paidCard: number = 0
    let paidDeposit: number = 0

    for (let item of payments.value) {
      let paidChf: number = isNaN(Number(item.amount_chf)) ? 0 : Number(item.amount_chf)
      let paid: number = isNaN(Number(item.amount)) ? 0 : Number(item.amount)
      let received: number = isNaN(Number(item.received)) ? 0 : Number(item.received)
      let change: number = Math.max(0, Number(item.change) || 0)

      paidTotal += paidChf
      if (item.method === 'cash') {
        receivedTotal += (item.currency_code === 'CHF') ? received : paidChf
        changeTotal += (item.currency_code === 'CHF') ? change : 0
      } else {
        receivedTotal += paidChf
      }
      debtTotal -= paidChf
      paidCash += (item.method === 'cash') ? paidChf : 0
      paidCard += (item.method === 'card') ? paidChf : 0
      paidDeposit += (item.method === 'deposit') ? paidChf : 0
    }

    return {total: total, paid_total: paidTotal, received_total: receivedTotal, change_total: changeTotal, debt_total: debtTotal, paid: {cash: paidCash, card: paidCard, deposit: paidDeposit}}
  }

  function accountingIdentifiedEntries(): AccountingEntry[] {
    return accountingCreditEntries?.filter((item) => (item.id_contact.length > 0 && item.id_contact === customerValue && item.debits.some((sub_item) => (sub_item?.reference?.id_treasury_account)))) ?? []
  }

  function accountingUnidentifiedEntries(): AccountingEntry[] {
    return accountingCreditEntries?.filter((item) => (item.id_contact.length === 0 && item.debits.some((sub_item) => (sub_item?.reference?.id_treasury_account)))) ?? []
  }

  function accountingWithoutTreasuryEntries(): AccountingEntry[] {
    return accountingCreditEntries?.filter((item) => (item.id_contact.length > 0 && item.id_contact === customerValue && ![...(item.credits || []), ...(item.debits || [])].some((sub_item) => (sub_item?.reference?.id_treasury_account)))) ?? []
  }

  useEffect(() => {
    let paymentsIsValid: boolean = payments.valid.length > 0 && payments.valid.every((item) => isValidObjectAttributes(item))
    const depositsMissingTreasury = payments.value.some(p => p.method === 'deposit' && !p.id_treasury_account)
    const paidTotal = payments.value.reduce((sum, p) => sum + (isNaN(Number(p.amount_chf)) ? 0 : Number(p.amount_chf)), 0)
    const amountMatch = Math.abs(paidTotal - totalAmount) < 0.01
    onPaymentsChange({
      payments: payments,
      isValid: payments.list && paymentsIsValid && amountMatch && !depositsMissingTreasury
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payments])

  useEffect(() => {
    if (payments.value.length > 0) {
      setPayments({list: false, value: [], valid: []})
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalAmount])

  return (
    <>
      <div className="row g-5 mb-5">
        <div className="col-md-4 col-lg-3">
          <h5 className="mb-5">{lang.labels.paymentMethods}</h5>
          <div className="d-flex flex-column gap-3">
            <button className="btn btn-light-info w-100 text-uppercase" type="button" disabled={!(calculatorPayment().debt_total > 0)} onClick={() => executeAddPayment('cash')}>{lang.labels.cash}</button>
            <button className="btn btn-light-success w-100 text-uppercase" type="button" disabled={!(calculatorPayment().debt_total > 0)} onClick={() => executeAddPayment('card')}>{lang.labels.card}</button>
            <button className="btn btn-light-warning w-100 text-uppercase" type="button" disabled={!(calculatorPayment().debt_total > 0)} onClick={() => executeAddPayment('deposit')}>{lang.labels.deposit}</button>
          </div>
        </div>
        <div className="col-md-8 col-lg-9">
          <h5 className="mb-5">{lang.labels.paymentCalculation}</h5>
          { payments.value.length === 0 && payments.valid.length === 0
            ?
            <>
              <div className="text-muted mb-5">{lang.labels.addPaymentUsingCashOrCardOrDeposit}</div>
              <div id="legend-payment-required" className="fv-plugins-message-container invalid-feedback d-none">
                <div data-field="contents" data-validator="notEmpty">{lang.labels.paymentsIsRequired}</div>
              </div>
            </>
            :
            <>
              { payments.value.map (( (item, index) => { return (
                <div key={index} className="border border-gray-300 rounded bg-light p-3 mb-2">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <div className="d-flex align-items-center">
                      <label className="fw-bolder">{lang.labels.payment} [{index + 1}]: {paymentMethodValue(lang, item.method)}</label>
                      { item.currency_code && item.currency_code !== 'CHF' &&
                        <span className="badge badge-sm badge-light-warning ms-2">{item.currency_code}</span>
                      }
                    </div>
                    <button className="btn btn-sm btn-icon btn-flex btn-light-primary ms-2" type="button" onClick={() => executeRemovePayment(index)}>
                      <span className="svg-icon svg-icon-3 p-3">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <path d="M5 9C5 8.44772 5.44772 8 6 8H18C18.5523 8 19 8.44772 19 9V18C19 19.6569 17.6569 21 16 21H8C6.34315 21 5 19.6569 5 18V9Z" fill="black" />
                          <path opacity="0.5" d="M5 5C5 4.44772 5.44772 4 6 4H18C18.5523 4 19 4.44772 19 5V5C19 5.55228 18.5523 6 18 6H6C5.44772 6 5 5.55228 5 5V5Z" fill="black" />
                          <path opacity="0.5" d="M9 4C9 3.44772 9.44772 3 10 3H14C14.5523 3 15 3.44772 15 4V4H9V4Z" fill="black" />
                        </svg>
                      </span>
                    </button>
                  </div>
                  { item.method === 'cash' && getAvailableCashCurrencies().length > 1 &&
                    <div className="row g-1 mb-2">
                      <div className="fv-row col-4">
                        <label className="form-label fs-7 mb-0">{lang.labels.currency}</label>
                        <select className="form-select form-select-sm" value={item.currency_code} onChange={(e) => handleChangeCurrencyPayment(index, e.target.value)}>
                          { getAvailableCashCurrencies().map((cur) => (
                            <option key={cur} value={cur}>{cur}</option>
                          ))}
                        </select>
                      </div>
                      { item.currency_code !== 'CHF' &&
                        <>
                          <div className="fv-row col-4">
                            <label className="form-label fs-7 mb-0">{lang.labels.exchangeRate ?? 'Exchange rate'}</label>
                            <input className="form-control form-control-sm" type="text" placeholder="0.00" value={item.exchange_rate} onChange={(e) => handleChangeExchangeRate(index, e)} />
                            { rateStaleWarning && ratesCache[item.currency_code]?.stale &&
                              <div className="text-warning fs-9 mt-1">{rateStaleWarning}</div>
                            }
                          </div>
                          <div className="fv-row col-4">
                            <label className="form-label fs-7 mb-0">{lang.labels.exchangeDate ?? 'Exchange date'}</label>
                            <input className="form-control form-control-sm" type="date" value={item.exchange_date} onChange={(e) => handleChangeExchangeDate(index, e)} />
                          </div>
                        </>
                      }
                    </div>
                  }
                  <div className="row g-1">
                    <div className={`fv-row ${item.method === "deposit" ? "col-6" : "d-none"}`}>
                      <label className="form-label fs-7 mb-0 required">{lang.labels.transaction}</label>
                      <Dropdown>
                        <Dropdown.Toggle variant="select2 select2-container select2-container--bootstrap5 select2-container--below select2-container--focus select2-container--open w-100 p-0">
                          <span className="selection">
                            <span className="select2-selection select2-selection--single form-select form-select-sm">
                              <span role="textbox">
                                {payments.valid[index].id_accounting_entry && item.id_accounting_entry ? formatAccountingEntry(lang, companyCurrency, item.id_accounting_entry, treasuryAccountsForCompany ?? [], "credit") : lang.labels.selectOption}
                              </span>
                            </span>
                          </span>
                        </Dropdown.Toggle>
                        <Dropdown.Menu variant="select2-container select2-container--bootstrap5 select2-container--open w-100">
                          <span className="select2-dropdown select2-dropdown--below">
                            <span className="select2-results">
                              <ul className="select2-results__options" role="listbox">
                                <li className={`select2-results__option select2-results__option--selectable ${accountingCreditEntries?.length > 0 && "d-none"}`}>
                                  <Dropdown.Item bsPrefix="select2-results__option__text">{lang.labels.selectOption}</Dropdown.Item>
                                </li>
                                { accountingIdentifiedEntries().length > 0 &&
                                  <>
                                    <span className={`select2-results__option select2-results__option--group ${accountingUnidentifiedEntries().length === 0 && accountingWithoutTreasuryEntries().length === 0 && "d-none"}`}>
                                      <Dropdown.Header bsPrefix="select2-results__option text-dark fw-bold">{lang.labels.identifiedTransactions}</Dropdown.Header>
                                    </span>
                                    <ul className="select2-results__options mh-100px mb-2" role="listbox">
                                      { accountingIdentifiedEntries().map (( (other_item, other_index) => { return (
                                        <li key={other_index} className={`select2-results__option select2-results__option--selectable ${other_item.id === item.id_accounting_entry && "select2-results__option--selected"}`} role="option" aria-selected={other_item.id === item.id_accounting_entry}>
                                          <Dropdown.Item bsPrefix="select2-results__option__text" onClick={() => handleChangeAccountingEntry(index, other_item)}>{formatAccountingEntry(lang, companyCurrency, other_item, treasuryAccountsForCompany ?? [], "credit")}</Dropdown.Item>
                                        </li>
                                      )}))}
                                    </ul>
                                  </>
                                }
                                { accountingWithoutTreasuryEntries().length > 0 &&
                                  <>
                                    <span className={`select2-results__option select2-results__option--group ${accountingUnidentifiedEntries().length === 0 && accountingIdentifiedEntries().length === 0 && "d-none"}`}>
                                      <Dropdown.Header bsPrefix="select2-results__option text-dark fw-bold">{lang.labels.creditNote}</Dropdown.Header>
                                    </span>
                                    <ul className="select2-results__options mh-100px" role="listbox">
                                      { accountingWithoutTreasuryEntries().map (( (other_item, other_index) => { return (
                                        <li key={other_index} className={`select2-results__option select2-results__option--selectable ${other_item.id === item.id_accounting_entry && "select2-results__option--selected"}`} role="option" aria-selected={other_item.id === item.id_accounting_entry}>
                                          <Dropdown.Item bsPrefix="select2-results__option__text" onClick={() => handleChangeAccountingEntry(index, other_item)}>{formatAccountingEntry(lang, companyCurrency, other_item, treasuryAccountsForCompany ?? [], "credit")}</Dropdown.Item>
                                        </li>
                                      )}))}
                                    </ul>
                                  </>
                                }
                                { accountingUnidentifiedEntries().length > 0 &&
                                  <>
                                    <span className={`select2-results__option select2-results__option--group ${accountingIdentifiedEntries().length === 0 && accountingWithoutTreasuryEntries().length === 0 && "d-none"}`}>
                                      <Dropdown.Header bsPrefix="select2-results__option text-dark fw-bold">{lang.labels.unidentifiedTransactions}</Dropdown.Header>
                                    </span>
                                    <ul className="select2-results__options mh-100px" role="listbox">
                                      { accountingUnidentifiedEntries().map (( (other_item, other_index) => { return (
                                        <li key={other_index} className={`select2-results__option select2-results__option--selectable ${other_item.id === item.id_accounting_entry && "select2-results__option--selected"}`} role="option" aria-selected={other_item.id === item.id_accounting_entry}>
                                          <Dropdown.Item bsPrefix="select2-results__option__text" onClick={() => handleChangeAccountingEntry(index, other_item)}>{formatAccountingEntry(lang, companyCurrency, other_item, treasuryAccountsForCompany ?? [], "credit")}</Dropdown.Item>
                                        </li>
                                      )}))}
                                    </ul>
                                  </>
                                }
                              </ul>
                            </span>
                          </span>
                        </Dropdown.Menu>
                      </Dropdown>
                      <AppLegend attribute={{check: payments.valid[index].id_accounting_entry, name: "payment-accounting-entry", index: index, sub_index: null}} container={{valid: false, required: true, identical: false, exists: false}}></AppLegend>
                    </div>
                    <div className={`fv-row ${item.method === "cash" ? "col-4" : "col-6"}`}>
                      <label className="form-label fs-7 mb-0 required">{lang.labels.amount}</label>
                      <div className="position-relative">
                        <div className="position-absolute translate-middle-y top-50 start-0 form-control bg-transparent border-0 border-end rounded-0 rounded-start fw-bolder fs-8 text-center w-45px h-auto px-1">{item.method === 'cash' && item.currency_code !== 'CHF' ? item.currency_code : companyCurrency.symbol}</div>
                        <input id={`input-payment-amount${suffixLegend(index)}`} className={`form-control form-control-sm ps-15 ${item.method === 'card' ? 'bg-light' : ''}`} type="text" autoComplete="off" name="amount" placeholder="0.00" value={item.amount} readOnly={item.method === 'card'} onChange={(event) => handleChangeAmountPayment(index, event)} onKeyUp={() => validateAmountPayment(index)} onBlur={() => validateAmountPayment(index)} />
                      </div>
                      <AppLegend attribute={{check: payments.valid[index].amount, name: "payment-amount", index: index, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
                    </div>
                    <div className={`fv-row ${item.method === "cash" ? "col-4" : item.method === "card" ? "col-6" : "d-none"}`}>
                      <label className="form-label fs-7 mb-0 required">{lang.labels.received}</label>
                      <div className="position-relative">
                        <div className="position-absolute translate-middle-y top-50 start-0 form-control bg-transparent border-0 border-end rounded-0 rounded-start fw-bolder fs-8 text-center w-45px h-auto px-1">{item.method === 'cash' && item.currency_code !== 'CHF' ? item.currency_code : companyCurrency.symbol}</div>
                        <input id={`input-payment-received${suffixLegend(index)}`} className={`form-control form-control-sm ps-15 ${item.method !== 'cash' ? 'bg-light' : ''}`} type="text" autoComplete="off" name="amount" placeholder="0.00" value={item.received} readOnly={item.method !== 'cash'} onChange={(event) => handleChangeReceivedPayment(index, event)} onKeyUp={() => validateReceivedPayment(index)} onBlur={() => validateReceivedPayment(index)} />
                      </div>
                      <AppLegend attribute={{check: payments.valid[index].received, name: "payment-received", index: index, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
                    </div>
                    <div className={`fv-row ${item.method === "cash" ? "col-4" : "d-none"}`}>
                      <label className="form-label fs-7 mb-0">{lang.labels.change}</label>
                      <div className="position-relative">
                        { item.currency_code !== 'CHF'
                          ?
                          <select className="position-absolute translate-middle-y top-50 start-0 form-control bg-transparent border-0 border-end rounded-0 rounded-start fw-bolder fs-8 text-center w-60px h-auto px-1" style={{zIndex: 1, cursor: 'pointer'}} value={item.change_currency_code} onChange={(e) => handleChangeChangeCurrency(index, e.target.value)}>
                            <option value="CHF">CHF</option>
                            <option value={item.currency_code}>{item.currency_code}</option>
                          </select>
                          :
                          <div className="position-absolute translate-middle-y top-50 start-0 form-control bg-transparent border-0 border-end rounded-0 rounded-start fw-bolder fs-8 text-center w-45px h-auto px-1">{companyCurrency.symbol}</div>
                        }
                        <span className={`form-control form-control-sm text-end ${item.currency_code !== 'CHF' ? 'ps-17' : 'ps-15'}`}>{formatChangeAmount(item)}</span>
                      </div>
                    </div>
                  </div>
                  { item.method === 'cash' && item.currency_code !== 'CHF' && item.exchange_rate &&
                    <div className="mt-2 text-muted fs-8">
                      {lang.labels.equivalentCHF ?? 'Equivalent CHF'}: CHF {formatMoney(Number(item.amount_chf))}
                    </div>
                  }
                </div>
              )}))}
            </>
          }
        </div>
      </div>
      <div className="d-flex justify-content-end">
        <div className="mw-500px">
          <div className="d-flex flex-stack px-5 py-2 bg-light-info border-top border-bottom">
            <div className="fw-bold pe-20 text-secondary fs-7">{lang.labels.total}:</div>
            <div className="text-end fw-bolder ps-20">{companyCurrency.symbol} {formatMoney(calculatorPayment().total)}</div>
          </div>
          <div className="d-flex flex-stack px-5 py-2 bg-light-warning border-bottom">
            <div className="fw-bold pe-20 text-secondary fs-7">{lang.labels.received}:</div>
            <div className="text-end fw-bolder ps-20">{companyCurrency.symbol} {formatMoney(calculatorPayment().received_total)}</div>
          </div>
          <div className="d-flex flex-stack px-5 py-2 bg-light-warning border-bottom">
            <div className="fw-bold pe-20 text-secondary fs-7">{lang.labels.change}:</div>
            <div className="text-end fw-bolder ps-20">{companyCurrency.symbol} {formatMoney(calculatorPayment().change_total)}</div>
          </div>
          <div className="d-flex flex-stack px-5 py-2 bg-light-success border-top border-bottom">
            <div className="fw-bold pe-20 text-secondary fs-7">{lang.labels.payed}:</div>
            <div className="text-end fw-bolder ps-20">{companyCurrency.symbol} {formatMoney(calculatorPayment().paid_total)}</div>
          </div>
          { calculatorPayment().paid.cash > 0 &&
            <div className="d-flex flex-stack ps-10 pe-5 py-1 bg-light-dark">
              <div className="fw-bold pe-20 text-secondary fs-8">{lang.labels.cash}</div>
              <div className="text-end fw-bolder ps-20 fs-7">{companyCurrency.symbol} {formatMoney(calculatorPayment().paid.cash)}</div>
            </div>
          }
          { calculatorPayment().paid.card > 0 &&
            <div className="d-flex flex-stack ps-10 pe-5 py-1 bg-light-dark">
              <div className="fw-bold pe-20 text-secondary fs-8">{lang.labels.card}</div>
              <div className="text-end fw-bolder ps-20 fs-7">{companyCurrency.symbol} {formatMoney(calculatorPayment().paid.card)}</div>
            </div>
          }
          { calculatorPayment().paid.deposit > 0 &&
            <div className="d-flex flex-stack ps-10 pe-5 py-1 bg-light-dark">
              <div className="fw-bold pe-20 text-secondary fs-8">{lang.labels.deposit}</div>
              <div className="text-end fw-bolder ps-20 fs-7">{companyCurrency.symbol} {formatMoney(calculatorPayment().paid.deposit)}</div>
            </div>
          }
          <div className="d-flex flex-stack px-5 py-2 bg-light-danger border-top border-bottom">
            <div className="fw-bold pe-20 text-secondary fs-7">{lang.labels.debt}:</div>
            <div className="text-end fw-bolder ps-20">{companyCurrency.symbol} {formatMoney(calculatorPayment().debt_total)}</div>
          </div>
        </div>
      </div>
    </>
  )
}

export default PosPaymentForm;
